<template>
  <div>
    <div
      ref="chart"
      :class="'js_export_report_dymanic_chart'"
      :style="showStub ? {pointerEvents: 'none'} : null"
    />
    <div
      ref="legend"
      :class="[$style.legend, 'export_html_legend']"
      :style="{width: width, left: leftPos}"
    >
      <div
        v-for="(item, index) in legendItems"
        :key="index"
        :class="[
          $style.wrapper,
          'export_html_legend_item',
          disabledSeries[item.name] && 'disabled'
        ]"
        @click="toggleSelect(item.name)"
      >
        <div
          :class="[$style.icon, 'export_html_icon']"
          :style="{color: item.color}"
          v-html="svgLegendItem"
        />
        <div :class="[$style.name, 'export_html_name']">
          {{ item.label }}
        </div>
      </div>
    </div>
    <div
      v-if="showStub && showStubMessage"
      :class="$style.stub_description"
      :style="{width: width, left: leftPos}"
    >
      <div :class="$style.wrapper">
        <span :class="$style.text">
          {{ locales(`report_page_no_filtered_data`) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from '../../../libs/echarts';
import charts from '../progress/chartsConfig';

const svgLegendItem = `<svg width="34" height="14" viewBox="0 0 34 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="Frame 33235">
  <circle id="Ellipse 1114" cx="17" cy="7" r="6.5" fill="currentColor" stroke="currentColor"/>
  <line id="Line 8" x1="1.6756e-07" y1="7" x2="34" y2="7" stroke="currentColor" stroke-width="4"/>
  </g>
</svg>`;

export default {
  props: {
    config: { type: Object, required: true },
    isStatic: { type: Boolean, required: true, default: false },
    showStubMessage: { type: Boolean, required: false, default: true },
  },
  emits: {
    onAllLegendsDisabled: null,
  },
  data() {
    return {
      id: 'chart',
      locales: __,
      width: 'auto',
      leftPos: 0,
      disabledSeries: {},
      legendItems: [],
      timeout: null,
      localConfig: null,
    };
  },
  computed: {
    leftSidebarWidth() {
      return this.$store.getters.leftSidebarWidth;
    },
    svgLegendItem() {
      return svgLegendItem;
    },
    allLegendItemsDisabled() {
      return this.legendItems.every(item => this.disabledSeries[item.name]);
    },
    showStub() {
      return this.isStatic || this.allLegendItemsDisabled;
    },
  },
  watch: {
    config: {
      handler(conf) {
        if (this.allLegendItemsDisabled) return;

        setTimeout(() => {
          this.localConfig = { ...conf };
          this.instance.setOption(conf);
          this.setLegendItems();
        }, 0);
      },
      immediate: true,
    },
    isStatic: {
      handler(flag) {
        if (!this.instance) {
          return;
        }

        if (flag) {
          this.instance.dispatchAction({
            type: 'legendAllSelect',
          });
          this.disabledSeries = {};
          this.setLegendItems();
        }

        setTimeout(() => this.onResize(flag), 0);
      },
      immediate: true,
    },
    leftSidebarWidth() {
      this.onResize();
    },
    allLegendItemsDisabled(flag) {
      this.$emit('onAllLegendsDisabled', flag);
    },
  },
  mounted() {
    this.instance = echarts.init(this.$refs.chart);
    this.localConfig = { ...this.config };
    this.instance.setOption(this.config);
    this.setLegendItems();
    this.onResize();

    window.addEventListener('resize', this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    echarts.dispose(this.instance);
  },
  methods: {
    onResize() {
      const width = document.documentElement.clientWidth - this.leftSidebarWidth;
      const height = document.documentElement.clientHeight - 200;

      this.instance.resize({ width, height });
      this.width = `${width}px`;
      this.leftPos = `${this.leftSidebarWidth}px`;
    },
    setLegendItems() {
      const legends = this.config.series;

      this.legendItems = legends.map((item, index) => ({
        name: item.name,
        label: __(`${item.label()}`),
        color: this.disabledSeries[item.name] ? this.config.series[index].disabledColor : this.config.series[index].color,
        type: item.type,
      }));
    },
    toggleSelect(name) {
      if (this.isStatic) {
        return;
      }

      const activeSeriesCountBefore = this.legendItems.filter(item => !this.disabledSeries[item.name]).length;

      if (name in this.disabledSeries) {
        delete this.disabledSeries[name];
      } else {
        this.disabledSeries[name] = true;
      }

      this.setLegendItems();

      this.$nextTick(() => {
        const activeSeriesCountAfter = this.legendItems.filter(item => !this.disabledSeries[item.name]).length;

        if (!activeSeriesCountAfter) {
          const updatedConfig = { ...charts.monthProgressChart().stub };

          updatedConfig.legend.selected = this.legendItems.reduce((acc, item) => {
            acc[item.name] = true;

            return acc;
          }, {});
          this.instance.setOption(updatedConfig);
        } else if (!activeSeriesCountBefore) {
          const updatedConfig = { ...this.localConfig };

          updatedConfig.legend.selected = this.legendItems.reduce((acc, item) => {
            acc[item.name] = !this.disabledSeries[item.name];

            return acc;
          }, {});
          this.instance.setOption(updatedConfig);
        } else {
          this.instance.dispatchAction({
            type: 'legendToggleSelect',
            name,
          });
        }
      });
    },
  },
};
</script>
<style module lang="less">
@import '../../../less/_mixins.less';
@import '../../../less/_fonts.less';
.stub_description {
  display: flex;
  justify-content: center;
  position: fixed;
  top: 50%;
  transform: translateY(-50%);

  .wrapper {
    background: #fff;
    padding: 10px;

    .text {
      margin: 0 auto;
      .FontLatoRegular;
      font-size: 18px;
      text-align: center;
      opacity: .5;
    }
  }
}

.legend {
  display: flex;
  align-items: center;
  justify-content: center;

  position: fixed;
  bottom: 52px;

  .wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    .icon {
      display: flex;
      align-items: center;
    }

    .name {
      .FontLatoRegular;
      font-size: 16px;
      color: #191919;
      padding-left: 8px;
    }

    &:not(:last-child) {
      margin-right: 24px;
    }
  }
}
</style>
